import React from 'react';
import { Icon } from '@iconify/react';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import { resolveFourStats } from '@/components/client/mobile/RaporEventCard';

export interface RaporEventStoryCardProps {
  playerName: string;
  playerAvatarUrl?: string;
  eventName: string;
  eventDateFormatted: string;
  location?: string;
  sportCategory?: string;
  deltaMMR?: number | null;
  goals: number;
  assists: number;
  wins: number;
  matches: number;
  point?: number;
  pointLoss?: number;
  isRacket?: boolean;
  selectedStatKeys?: string[];
  rawStats?: any;
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_AVATAR = 'https://cdn.sporto.id/default-avatar.png';
const SPORTO_LOGO_URL = 'https://cdn.sporto.id/sporto.svg';
const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';

export const RaporEventStoryCard: React.FC<RaporEventStoryCardProps> = ({
  playerName,
  playerAvatarUrl,
  eventName,
  eventDateFormatted,
  location,
  sportCategory = 'FUTSAL',
  deltaMMR,
  goals,
  assists,
  wins,
  matches,
  point,
  pointLoss,
  isRacket = false,
  selectedStatKeys,
  rawStats,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const formattedDate = formatDateDDMMYYYY(eventDateFormatted);
  const dateLoc = `${formattedDate}${location ? ` · ${location}` : ''}`;
  const avatarSrc = playerAvatarUrl || DEFAULT_AVATAR;
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;

  const statItems = resolveFourStats(
    selectedStatKeys,
    rawStats || { goals, assists, matches, wins, point, point_loss: pointLoss },
    isRacket
  );

  const mainStat1 = statItems[0] || { value: goals, label: 'Goals' };
  const mainStat2 = statItems[1] || { value: assists, label: 'Assists' };
  const secStat1 = statItems[2] || { value: wins, label: 'Game menang' };
  const secStat2 = statItems[3] || { value: matches, label: 'Game dimainkan' };

  const mmrVal = deltaMMR ?? 0;
  const mmrRounded = Math.round(mmrVal);
  const isMMRPositive = mmrRounded > 0;
  const isMMRNegative = mmrRounded < 0;

  const mmrBg = isMMRPositive
    ? 'bg-[#0BCF46]'
    : isMMRNegative
    ? 'bg-[#E11D48]'
    : 'bg-[#0E1726]/60 border border-white/20 backdrop-blur-sm';

  const mmrIcon = isMMRPositive
    ? 'ph:trend-up'
    : isMMRNegative
    ? 'ph:trend-down'
    : 'ph:equals';

  const mmrText = isMMRPositive ? `+${mmrRounded} MMR` : `${mmrRounded} MMR`;

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Optional Background Image (only if not transparent) */}
      {!transparent && (
        <img
          src={activeBgImage}
          alt="Foto pertandingan"
          className="absolute inset-0 w-full h-full object-cover object-[58%_50%]"
        />
      )}

      {/* Shadow Gradient Overlay (provides transparent contrast for text over InstaStory photo) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(14, 23, 38, 0.35) 0%, rgba(14, 23, 38, 0.15) 32%, rgba(14, 23, 38, 0.55) 62%, rgba(14, 23, 38, 0.95) 100%)',
        }}
      />

      {/* Content Container (Direct layout without card box, using text shadows) */}
      <div className="absolute inset-0 flex flex-col justify-end p-[28px]">
        <div className="flex flex-col gap-[22px]">
          {/* Category Pill, Event Title, Date/Location */}
          <div className="flex flex-col gap-[8px]">
            <div className="inline-flex self-start items-center bg-[#FC480D] rounded-[6px] px-[12px] py-[5px] shadow-md">
              <span className="text-[13px] font-bold text-white uppercase tracking-wide">
                {sportCategory}
              </span>
            </div>
            <div
              className="text-[34px] font-extrabold text-white leading-[40px] text-pretty"
              style={{ textShadow: '0 2px 12px rgba(14,23,38,0.85)' }}
            >
              {eventName}
            </div>
            <div
              className="text-[15px] font-medium text-white/80 truncate"
              style={{ textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
            >
              {dateLoc}
            </div>
          </div>

          {/* Huge Main Stats (Stat 1 & Stat 2) */}
          <div className="flex items-end gap-[28px]">
            <div className="flex flex-col min-w-0">
              <div
                className="text-[72px] font-extrabold text-white leading-[74px] tracking-[-0.03em] truncate"
                style={{ textShadow: '0 4px 16px rgba(14,23,38,0.85)' }}
              >
                {mainStat1.value}
              </div>
              <div
                className="text-[15px] font-semibold text-[#FFA96F] truncate"
                style={{ textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
              >
                {mainStat1.label}
              </div>
            </div>
            <div className="w-[1px] h-[56px] bg-white/30 mb-[6px] shrink-0" />
            <div className="flex flex-col min-w-0">
              <div
                className="text-[72px] font-extrabold text-white leading-[74px] tracking-[-0.03em] truncate"
                style={{ textShadow: '0 4px 16px rgba(14,23,38,0.85)' }}
              >
                {mainStat2.value}
              </div>
              <div
                className="text-[15px] font-semibold text-[#FFA96F] truncate"
                style={{ textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
              >
                {mainStat2.label}
              </div>
            </div>
          </div>

          {/* Glassmorphic Secondary Stats (Stat 3 & Stat 4) */}
          <div className="flex gap-[12px]">
            <div className="flex-1 bg-white/10 border border-white/20 rounded-[12px] p-[14px_16px] flex flex-col gap-[2px] backdrop-blur-[4px] shadow-lg min-w-0">
              <div className="text-[24px] font-extrabold text-white truncate">{secStat1.value}</div>
              <div className="text-[13px] font-medium text-white/80 truncate">{secStat1.label}</div>
            </div>
            <div className="flex-1 bg-white/10 border border-white/20 rounded-[12px] p-[14px_16px] flex flex-col gap-[2px] backdrop-blur-[4px] shadow-lg min-w-0">
              <div className="text-[24px] font-extrabold text-white truncate">{secStat2.value}</div>
              <div className="text-[13px] font-medium text-white/80 truncate">{secStat2.label}</div>
            </div>
          </div>

          {/* Footer Bar */}
          <div className="border-t border-white/20 pt-[18px] flex items-center justify-between">
            {/* Player Info */}
            <div className="flex items-center gap-[12px] min-w-0">
              <img
                src={avatarSrc}
                alt={playerName}
                className="w-[44px] h-[44px] rounded-full object-cover shrink-0 border-2 border-white/60 shadow-md"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                }}
              />
              <div className="flex flex-col min-w-0">
                <div
                  className="text-[16px] font-bold text-white truncate max-w-[180px]"
                  style={{ textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
                >
                  {playerName}
                </div>
                <div
                  className="text-[13px] font-medium text-white/70"
                  style={{ textShadow: '0 1px 6px rgba(14,23,38,0.8)' }}
                >
                  Player
                </div>
              </div>
            </div>

            {/* MMR Badge & Logo */}
            <div className="flex items-center gap-[16px] shrink-0">
              <div
                className={`inline-flex items-center gap-[6px] rounded-full px-[12px] py-[6px] shadow-md ${mmrBg}`}
              >
                <Icon icon={mmrIcon} className="text-white text-[13px]" />
                <span className="text-[12px] font-bold text-white">{mmrText}</span>
              </div>
              <img src={SPORTO_LOGO_URL} alt="Sporto" className="h-[22px] w-auto object-contain block" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaporEventStoryCard;
